import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Grid, Card, CardContent, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Button, Chip, AppBar, Toolbar, IconButton, Alert,
  FormControl, InputLabel, Select, MenuItem, Tooltip, Snackbar,
  Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, CardActionArea,
  TextField, InputAdornment, Backdrop
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import BusinessIcon from "@mui/icons-material/Business";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import InboxIcon from "@mui/icons-material/Inbox";
import axios from "axios";
import Footer from "../components/Footer";
import SupportFab from "../components/SupportFab";
import EmptyState from "../components/EmptyState";
import { getToken, getUser, clearSession, isTokenExpired, isIdleExpired, touchActivity } from "../utils/auth";
import { useIdleTimeout } from "../utils/useIdleTimeout";

// Legenda das fontes
const FONTE_INFO = {
  inovafarma: {
    label: "InovaFarma",
    color: "#2196f3",
    bgColor: "#e3f2fd",
    descricao: "Sistema InovaFarma (Precisao Sistemas) - Software de gestao para farmacias"
  },
  omie: {
    label: "Otimize",
    color: "#4caf50",
    bgColor: "#e8f5e9",
    descricao: "Otimize Informatica - TEF, Servicos e Produtos de Informatica"
  }
};

// Opcoes de periodo
const PERIODOS = {
  mesAtual: "Mes Atual",
  mesAnterior: "Mes Anterior",
  ultimos3Meses: "Ultimos 3 Meses",
  ultimos6Meses: "Ultimos 6 Meses",
  todos: "Todos"
};

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [user, setUser] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [boletos, setBoletos] = useState({ inovafarma: [], omie: [] });
  const [notas, setNotas] = useState({ inovafarma: [], omie: [] });
  const [cnpjFilter, setCnpjFilter] = useState("todos");
  const [fonteFilter, setFonteFilter] = useState("todos");
  const [periodoFilter, setPeriodoFilter] = useState("ultimos6Meses");
  const [busca, setBusca] = useState("");
  const [clientes, setClientes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [showEmpresas, setShowEmpresas] = useState(false);
  const [boletoFilter, setBoletoFilter] = useState("todos");

  const token = getToken();

  const handleLogout = useCallback((reason) => {
    clearSession();
    if (reason === "idle") {
      navigate("/?reason=idle", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useIdleTimeout(() => handleLogout("idle"));

  useEffect(() => {
    if (!token || isTokenExpired(token)) { handleLogout(); return; }
    if (isIdleExpired()) { handleLogout("idle"); return; }
    touchActivity();
    const stored = getUser();
    if (stored) setUser(stored);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [resumoRes, boletosRes, notasRes, clientesRes] = await Promise.all([
        axios.get("/api/customer-portal/resumo"),
        axios.get("/api/customer-portal/boletos"),
        axios.get("/api/customer-portal/notas"),
        axios.get("/api/customer-portal/clientes"),
      ]);
      setResumo(resumoRes.data);
      setBoletos(boletosRes.data);
      setNotas(notasRes.data);
      setClientes(clientesRes.data.clientes || []);
    } catch (err) {
      // 401 ja tratado pelo interceptor global em utils/auth
      if (err.response?.status !== 401) {
        setError("Não foi possível carregar seus dados. Tente novamente em instantes.");
      }
    } finally { setLoading(false); }
  };

  const parseBrDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes("/")) {
      const [day, month, year] = dateStr.split("/");
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    if (dateStr.includes("/")) return dateStr;
    const d = parseBrDate(dateStr);
    return d ? d.toLocaleDateString("pt-BR") : "-";
  };

  const formatCurrency = (v) => v ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v) : "R$ 0,00";

  const formatCnpj = (c) => c ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "-";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: "Linha digitavel copiada!" });
  };

  const getClienteName = (cnpj) => {
    const cliente = clientes.find(c => c.cnpj === cnpj);
    return cliente ? cliente.razaoSocial || cliente.nomeFantasia : formatCnpj(cnpj);
  };

  // Filtrar por periodo
  const filtrarPorPeriodo = (items, campoData) => {
    if (periodoFilter === "todos") return items;
    const hoje = new Date();
    const inicioMesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const inicioMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const fimMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
    const inicio3Meses = new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1);
    const inicio6Meses = new Date(hoje.getFullYear(), hoje.getMonth() - 5, 1);

    return items.filter(item => {
      const data = parseBrDate(item[campoData]);
      if (!data) return true;
      switch (periodoFilter) {
        case "mesAtual": return data >= inicioMesAtual;
        case "mesAnterior": return data >= inicioMesAnterior && data <= fimMesAnterior;
        case "ultimos3Meses": return data >= inicio3Meses;
        case "ultimos6Meses": return data >= inicio6Meses;
        default: return true;
      }
    });
  };

  // Filtrar por busca
  const filtrarPorBusca = (items) => {
    if (!busca.trim()) return items;
    const termo = busca.toLowerCase().trim();
    return items.filter(item => {
      const numero = (item.numero || item.parcela || "").toString().toLowerCase();
      const cnpj = (item.clienteCnpj || "").toLowerCase();
      return numero.includes(termo) || cnpj.includes(termo);
    });
  };

  // Funcao para download de boleto com loading
  const downloadBoleto = async (boleto, index) => {
    setDownloading("boleto_" + index);
    try {
      if (boleto.urlBoleto) {
        window.open(boleto.urlBoleto, "_blank");
        await new Promise(r => setTimeout(r, 1500));
        return;
      }
      const source = boleto.fonte === "inovafarma" ? "inovafarma" : "otimize";
      const response = await axios.get(
        `/api/customers/${source}/boletos/${boleto.id}/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `boleto_${boleto.numero || boleto.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Não foi possível baixar o boleto. Tente novamente." });
    } finally {
      setDownloading(null);
    }
  };

  // Funcao para download de nota com loading
  const downloadNota = async (nota, index) => {
    setDownloading("nota_" + index);
    try {
      if (nota.urlPdf) {
        window.open(nota.urlPdf, "_blank");
        await new Promise(r => setTimeout(r, 1500));
        return;
      }
      const source = nota.fonte === "inovafarma" ? "inovafarma" : "otimize";
      const response = await axios.get(
        `/api/customers/${source}/notas/${nota.id}/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `nota_${nota.numero || nota.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 404) {
        setSnackbar({ open: true, message: "PDF não disponível. Consulte no portal da prefeitura." });
      } else {
        setSnackbar({ open: true, message: "Não foi possível baixar a nota. Tente novamente." });
      }
    } finally {
      setDownloading(null);
    }
  };

  // Componente FonteChip
  const FonteChip = ({ fonte }) => {
    const info = FONTE_INFO[fonte] || { label: fonte, color: "#757575", bgColor: "#f5f5f5", descricao: "" };
    return (
      <Tooltip title={info.descricao} arrow>
        <Chip
          label={info.label}
          size="small"
          sx={{ backgroundColor: info.bgColor, color: info.color, fontWeight: "bold", cursor: "help" }}
        />
      </Tooltip>
    );
  };

  if (loading) return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "100vh", gap: 2 }}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">Carregando seus dados…</Typography>
    </Box>
  );

  const allBoletos = [...boletos.inovafarma.map(b => ({...b, fonte: "inovafarma"})), ...boletos.omie.map(b => ({...b, fonte: "omie"}))];
  const allNotas = [...notas.inovafarma.map(n => ({...n, fonte: "inovafarma"})), ...notas.omie.map(n => ({...n, fonte: "omie"}))];

  const hoje = new Date();
  const boletosPendentes = allBoletos.filter(b => {
    if (b.status === "pago") return false;
    const venc = parseBrDate(b.dataVencimento);
    return venc && venc >= hoje;
  });

  const sortByDate = (a, b, dateField) => {
    const dateA = parseBrDate(a[dateField]);
    const dateB = parseBrDate(b[dateField]);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateB - dateA;
  };

  // Aplicar todos os filtros aos boletos
  let boletosToShow = boletoFilter === "pendentes" ? boletosPendentes : allBoletos;
  if (cnpjFilter !== "todos") boletosToShow = boletosToShow.filter(b => b.clienteCnpj === cnpjFilter);
  if (fonteFilter !== "todos") boletosToShow = boletosToShow.filter(b => b.fonte === fonteFilter);
  boletosToShow = filtrarPorPeriodo(boletosToShow, "dataVencimento");
  boletosToShow = filtrarPorBusca(boletosToShow);
  boletosToShow = boletosToShow.sort((a, b) => sortByDate(a, b, "dataVencimento"));

  // Aplicar todos os filtros as notas
  let notasToShow = allNotas;
  if (cnpjFilter !== "todos") notasToShow = notasToShow.filter(n => n.clienteCnpj === cnpjFilter);
  if (fonteFilter !== "todos") notasToShow = notasToShow.filter(n => n.fonte === fonteFilter);
  notasToShow = filtrarPorPeriodo(notasToShow, "dataEmissao");
  notasToShow = filtrarPorBusca(notasToShow);
  notasToShow = notasToShow.sort((a, b) => sortByDate(a, b, "dataEmissao"));

  const handleCardClick = (type) => {
    if (type === "empresas") {
      setShowEmpresas(true);
    } else if (type === "pendentes") {
      setTab(0);
      setBoletoFilter("pendentes");
    } else if (type === "boletos") {
      setTab(0);
      setBoletoFilter("todos");
    } else if (type === "notas") {
      setTab(1);
    }
  };

  const limparFiltros = () => {
    setCnpjFilter("todos");
    setFonteFilter("todos");
    setPeriodoFilter("ultimos6Meses");
    setBusca("");
    setBoletoFilter("todos");
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
      {/* Backdrop de loading para download */}
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={downloading !== null}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Baixando PDF...</Typography>
        </Box>
      </Backdrop>

      <AppBar position="sticky" color="primary">
        <Toolbar>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: "8px",
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 16, letterSpacing: 0.5
            }}>O</Box>
            <Box sx={{ lineHeight: 1.1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Portal do Cliente</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Otimize Informática</Typography>
            </Box>
          </Box>
          {user?.nome && (
            <Box sx={{ mr: 2, textAlign: "right", display: { xs: "none", sm: "block" } }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.nome}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>{user.cnpjs?.length || 0} empresa{(user.cnpjs?.length || 0) === 1 ? "" : "s"}</Typography>
            </Box>
          )}
          <Tooltip title="Sair">
            <IconButton color="inherit" onClick={() => handleLogout()}><LogoutIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 3, flexGrow: 1, maxWidth: 1280, width: "100%", mx: "auto" }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Legenda das Fontes */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#fafafa" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <InfoIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">Legenda das Fontes</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip label="InovaFarma" size="small" sx={{ backgroundColor: FONTE_INFO.inovafarma.bgColor, color: FONTE_INFO.inovafarma.color, fontWeight: "bold" }} />
                <Typography variant="body2" color="textSecondary">Sistema InovaFarma (Precisao Sistemas) - Software de gestao para farmacias</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip label="Otimize" size="small" sx={{ backgroundColor: FONTE_INFO.omie.bgColor, color: FONTE_INFO.omie.color, fontWeight: "bold" }} />
                <Typography variant="body2" color="textSecondary">Otimize Informatica - TEF, Servicos e Produtos de Informatica</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: "pointer", "&:hover": { boxShadow: 6 } }} onClick={() => handleCardClick("empresas")}>
              <CardActionArea>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <BusinessIcon sx={{ fontSize: 40, color: "#1976d2", mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary">Empresas</Typography>
                      <Typography variant="h4">{user?.cnpjs?.length || 0}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: "pointer", "&:hover": { boxShadow: 6 }, bgcolor: boletoFilter === "pendentes" ? "#fff3e0" : "white" }} onClick={() => handleCardClick("pendentes")}>
              <CardActionArea>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PaymentIcon sx={{ fontSize: 40, color: "#f57c00", mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary">Boletos a Vencer</Typography>
                      <Typography variant="h4">{boletosPendentes.length}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: "pointer", "&:hover": { boxShadow: 6 }, bgcolor: boletoFilter === "todos" && tab === 0 ? "#e8f5e9" : "white" }} onClick={() => handleCardClick("boletos")}>
              <CardActionArea>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PaymentIcon sx={{ fontSize: 40, color: "#4caf50", mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary">Total Boletos</Typography>
                      <Typography variant="h4">{allBoletos.length}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: "pointer", "&:hover": { boxShadow: 6 }, bgcolor: tab === 1 ? "#f3e5f5" : "white" }} onClick={() => handleCardClick("notas")}>
              <CardActionArea>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: "#9c27b0", mr: 2 }} />
                    <Box>
                      <Typography color="textSecondary">Notas Fiscais</Typography>
                      <Typography variant="h4">{allNotas.length}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => { setTab(v); if (v === 1) setBoletoFilter("todos"); }}>
            <Tab label={"Boletos (" + boletosToShow.length + ")"} />
            <Tab label={"Notas (" + notasToShow.length + ")"} />
          </Tabs>
        </Paper>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar numero..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Periodo</InputLabel>
                <Select value={periodoFilter} onChange={(e) => setPeriodoFilter(e.target.value)} label="Periodo">
                  {Object.entries(PERIODOS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Fonte</InputLabel>
                <Select value={fonteFilter} onChange={(e) => setFonteFilter(e.target.value)} label="Fonte">
                  <MenuItem value="todos">Todas</MenuItem>
                  <MenuItem value="inovafarma">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: FONTE_INFO.inovafarma.color }} />
                      InovaFarma
                    </Box>
                  </MenuItem>
                  <MenuItem value="omie">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: FONTE_INFO.omie.color }} />
                      Otimize
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Empresa</InputLabel>
                <Select value={cnpjFilter} onChange={(e) => setCnpjFilter(e.target.value)} label="Empresa">
                  <MenuItem value="todos">Todas</MenuItem>
                  {user?.cnpjs?.map(cnpj => (
                    <MenuItem key={cnpj} value={cnpj}>{getClienteName(cnpj)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {tab === 0 && (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={boletoFilter} onChange={(e) => setBoletoFilter(e.target.value)} label="Status">
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="pendentes">A vencer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={tab === 0 ? 2 : 4}>
              <Button variant="outlined" fullWidth onClick={limparFiltros}>Limpar Filtros</Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Boletos */}
        {tab === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell><strong>Fonte</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Numero</strong></TableCell>
                <TableCell><strong>Vencimento</strong></TableCell>
                <TableCell align="right"><strong>Valor</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Acoes</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {boletosToShow.length === 0 ? <TableRow><TableCell colSpan={7}>
                  <EmptyState
                    icon={<PaymentIcon />}
                    title={allBoletos.length === 0 ? "Nenhum boleto disponível" : "Nenhum boleto para os filtros atuais"}
                    subtitle={allBoletos.length === 0 ? "Quando houver boletos emitidos, eles aparecerão aqui." : "Ajuste os filtros ou clique em \"Limpar Filtros\"."}
                  />
                </TableCell></TableRow> :
                  boletosToShow.map((b, i) => (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                      <TableCell><FonteChip fonte={b.fonte} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{getClienteName(b.clienteCnpj)}</Typography>
                        <Typography variant="caption" color="textSecondary">{formatCnpj(b.clienteCnpj)}</Typography>
                      </TableCell>
                      <TableCell>{b.numero || b.parcela || "-"}</TableCell>
                      <TableCell>{formatDate(b.dataVencimento)}</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(b.valor)}</strong></TableCell>
                      <TableCell>
                        <Chip label={b.status || "pendente"} color={b.status === "pago" ? "success" : b.status === "vencido" ? "error" : "warning"} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {b.linhaDigitavel && (
                            <Tooltip title={"Copiar: " + b.linhaDigitavel}>
                              <IconButton size="small" onClick={() => copyToClipboard(b.linhaDigitavel)}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Baixar PDF">
                            <IconButton size="small" color="primary" onClick={() => downloadBoleto(b, i)} disabled={downloading !== null}>
                              {downloading === "boleto_" + i ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tabela de Notas */}
        {tab === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell><strong>Fonte</strong></TableCell>
                <TableCell><strong>Empresa</strong></TableCell>
                <TableCell><strong>Numero</strong></TableCell>
                <TableCell><strong>Emissao</strong></TableCell>
                <TableCell align="right"><strong>Valor</strong></TableCell>
                <TableCell><strong>Tipo</strong></TableCell>
                <TableCell><strong>Acoes</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {notasToShow.length === 0 ? <TableRow><TableCell colSpan={7}>
                  <EmptyState
                    icon={<ReceiptIcon />}
                    title={allNotas.length === 0 ? "Nenhuma nota fiscal disponível" : "Nenhuma nota para os filtros atuais"}
                    subtitle={allNotas.length === 0 ? "As notas emitidas aparecerão aqui assim que sincronizadas." : "Ajuste os filtros ou clique em \"Limpar Filtros\"."}
                  />
                </TableCell></TableRow> :
                  notasToShow.map((n, i) => (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
                      <TableCell><FonteChip fonte={n.fonte} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{getClienteName(n.clienteCnpj)}</Typography>
                        <Typography variant="caption" color="textSecondary">{formatCnpj(n.clienteCnpj)}</Typography>
                      </TableCell>
                      <TableCell>{n.numero || "-"}</TableCell>
                      <TableCell>{formatDate(n.dataEmissao)}</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(n.valor)}</strong></TableCell>
                      <TableCell><Chip label={n.tipoNota || n.tipo || "nfe"} size="small" /></TableCell>
                      <TableCell>
                        <Tooltip title="Baixar PDF">
                          <IconButton size="small" color="primary" onClick={() => downloadNota(n, i)} disabled={downloading !== null}>
                            {downloading === "nota_" + i ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Resumo por Fonte */}
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Resumo por Fonte</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 1, bgcolor: FONTE_INFO.inovafarma.bgColor, borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: FONTE_INFO.inovafarma.color }}>{boletos.inovafarma.length}</Typography>
                <Typography variant="caption">Boletos InovaFarma</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 1, bgcolor: FONTE_INFO.omie.bgColor, borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: FONTE_INFO.omie.color }}>{boletos.omie.length}</Typography>
                <Typography variant="caption">Boletos Otimize</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 1, bgcolor: FONTE_INFO.inovafarma.bgColor, borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: FONTE_INFO.inovafarma.color }}>{notas.inovafarma.length}</Typography>
                <Typography variant="caption">Notas InovaFarma</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 1, bgcolor: FONTE_INFO.omie.bgColor, borderRadius: 1 }}>
                <Typography variant="h5" sx={{ color: FONTE_INFO.omie.color }}>{notas.omie.length}</Typography>
                <Typography variant="caption">Notas Otimize</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Dialog open={showEmpresas} onClose={() => setShowEmpresas(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suas Empresas</DialogTitle>
        <DialogContent>
          <List>
            {user?.cnpjs?.map(cnpj => {
              const cliente = clientes.find(c => c.cnpj === cnpj);
              return (
                <ListItem key={cnpj} divider>
                  <ListItemText primary={cliente?.razaoSocial || cliente?.nomeFantasia || "N/A"} secondary={formatCnpj(cnpj)} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />
      <SupportFab />
      <Footer />
    </Box>
  );
}

export default Dashboard;
